/** GA4 event `value`: `price` is per unit, so the total has to multiply by quantity. */
export const itemsValue = (items: { price: number; quantity: number }[]) => Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2));

interface ChangeItem {
  item_id: string;
  product_id: string;
  bundle_id?: string;
  quantity: number;
}

/** A bundle's component belongs to the bundle only, never to the same product bought on its own. */
export const isItemOf = (item: { product_id: string; bundle_id?: string }, productId: string) => (item.bundle_id ? item.bundle_id === productId : item.product_id === productId);

/** What one product's line change did to the cart, as GA4 add_to_cart / remove_from_cart items (a bundle counts per component). */
export const cartLineChange = <T extends ChangeItem>(before: T[], after: T[], productId: string) => {
  const ofProduct = (items: T[]) => new Map(items.filter((item) => isItemOf(item, productId)).map((item) => [`${item.bundle_id ?? ""}#${item.item_id}`, item]));
  const was = ofProduct(before);
  const now = ofProduct(after);
  const changed = [...new Set([...was.keys(), ...now.keys()])].flatMap((key) => {
    const item = now.get(key) ?? was.get(key);
    const delta = (now.get(key)?.quantity ?? 0) - (was.get(key)?.quantity ?? 0);
    return item && delta !== 0 ? [{ ...item, quantity: delta }] : [];
  });
  return {
    added: changed.filter((item) => item.quantity > 0),
    removed: changed.filter((item) => item.quantity < 0).map((item) => ({ ...item, quantity: -item.quantity })),
  };
};

export type LoyaltyLevel = "BRONZE" | "SILVER" | "GOLD" | "none";

/** GA4 user property `loyalty_level` of a signed-in account; a member without a level is BRONZE, as on the account page. */
export const loyaltyLevel = (account?: { isLoyaltyMember?: boolean; loyaltyMembershipStatus?: Exclude<LoyaltyLevel, "none"> }): LoyaltyLevel =>
  account?.isLoyaltyMember ? account.loyaltyMembershipStatus ?? "BRONZE" : "none";

/** GA4 `user_id`: the account id hashed, so no store identifier reaches Google as is. */
export const hashUserId = async (accountId: string) => {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(accountId));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
};
